import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    Inject,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import { Store } from 'redux';
import { Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';

import { CmpsDatePipe } from '../common/date.pipe';
import { StateToProps, storeObserver } from '../store';
import { STORE } from '../store.metadata';

import {
    CellNode,
    Column,
    GridComponent,
    RowNode,
    SortOrder,
} from './experimental.grid';
import { ColumnID, ColumnType, DataSource, RowType } from './table.tdcf.model';
import { formResetState } from './tdcf-form.action';
import { TranslationKeyMap } from './tdcf-form.i18n';
import { storeStateMapper, TableState } from './tdcf-form.state';
import {
    HierarchyInfo,
    isWithinCurrentDateRange,
    lastModifiedFor,
    Metadata,
    Mode,
    PayloadExtended,
    Status,
    statusFor,
} from './tdcf.model';

@Component({
    selector: '[cmpsTdcfTable]',
    templateUrl: './table.tdcf.component.html',
    host: {
        class: 'cmps-tdcf-table',
    },
})
export class TableTdcfComponent
    implements OnInit, AfterViewInit, OnDestroy, StateToProps<TableState>
{
    @ViewChild('grid', { static: true }) private readonly _grid: GridComponent;
    @ViewChild('thTpl', { static: true })
    private readonly _thTpl: TemplateRef<any>;

    @ViewChild('tdTpl', { static: true })
    private readonly _tdTpl: TemplateRef<any>;

    @ViewChild('tdFormTpl', { static: true })
    private readonly _tdFormTpl: TemplateRef<any>;

    @ViewChild('noDataTpl', { static: true })
    private readonly _noDataTpl: TemplateRef<any>;

    @Input() mode: Mode;
    @Input() templateId: number;
    @Input() metadata: Metadata;
    @Input() info: HierarchyInfo;

    i18n = TranslationKeyMap;
    ColumnType = ColumnType;
    RowType = RowType;
    Status = Status;
    private _storeSub: Subscription;
    state: TableState;
    dataSource: DataSource;

    constructor(
        private readonly _ngZone: NgZone,
        private readonly _changeDetectorRef: ChangeDetectorRef,
        @Inject(STORE) private readonly _store: Store,
        private readonly _datePipe: CmpsDatePipe,
    ) {}

    ngOnInit(): void {
        this._storeSub = storeObserver<TableState>(
            this._store,
            (state) => storeStateMapper(state).table,
        )
            .pipe(
                tap((state) =>
                    this._ngZone.run(() => {
                        this.state = state;
                        this.stateToProps();
                    }),
                ),
            )
            .subscribe();

        this.dataSource = new DataSource();
    }

    ngAfterViewInit(): void {
        this.dataSource.thTpl = this._thTpl;
        this.dataSource.tdTpl = this._tdTpl;
        this.dataSource.tdFormTpl = this._tdFormTpl;
        this.dataSource.noDataTpl = this._noDataTpl;
        this.dataSource.state = this.state;
        this.dataSource.setData(
            this.mode,
            this.metadata.reviewDateRanges,
            this.info,
        );

        this._changeDetectorRef.detectChanges();

        this._grid.grid.setChildren(this.dataSource.headerRows);
        this._grid.grid.appendChildren(this.dataSource.makeRows());

        const initial =
            this.mode === Mode.MEMBER
                ? ColumnID.LAST_MODIFIED.toString()
                : ColumnID.NAME.toString();
        this.onSortClick(this.dataSource.columns.find((c) => c.id === initial));
    }

    ngOnDestroy(): void {
        if (this._storeSub) {
            this._storeSub.unsubscribe();
        }
    }

    stateToProps(): void {
        if (this.dataSource) {
            this.dataSource.state = this.state;

            this._grid.grid.appendChildren(this.dataSource.makeRows());
        }
    }

    columnName(column: Column): string {
        switch (column.id) {
            case ColumnID.QUARTER.toString():
                return TranslationKeyMap._201_QUARTER;
            case ColumnID.LAST_MODIFIED.toString():
                return TranslationKeyMap._7219_DATE_UPDATED;
            case ColumnID.STATUS.toString():
                return TranslationKeyMap._127_STATUS;
            case ColumnID.NAME.toString():
                return TranslationKeyMap._53_NAME;
        }
        return null;
    }

    cellValue(
        column: Column,
        cell: CellNode,
        r: RowNode,
        columnType?: ColumnType,
    ): any {
        const type = columnType || cell.def.columnType;
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        const threadGuid =
            r.def.type === RowType.USER
                ? // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                  cell.def.ctx.payload && cell.def.ctx.payload.threadGuid
                : cell.def.ctx.threadGuid;
        switch (type) {
            case ColumnType.QUARTER:
                return cell.def.ctx.quarter || TranslationKeyMap._842_N_A;
            case ColumnType.LAST_MODIFIED:
                return (
                    (this.mode === Mode.MEMBER && !threadGuid
                        ? null
                        : this._datePipe.transform(
                              cell.def.ctx.lastModified,
                              'y-MM-dd h:mm a',
                          )) || TranslationKeyMap._842_N_A
                );
            case ColumnType.STATUS:
                switch (cell.def.ctx.status) {
                    case Status.UNKNOWN:
                        return TranslationKeyMap._7220_NOT_STARTED;
                    case Status.DRAFT:
                        return !threadGuid
                            ? TranslationKeyMap._7220_NOT_STARTED
                            : TranslationKeyMap._7221_DRAFT;
                    case Status.PENDING:
                        return TranslationKeyMap._7222_PENDING;
                    case Status.COMPLETE:
                        return TranslationKeyMap._7223_COMPLETED;
                }
                return null;
            case ColumnType.NAME:
                return cell.def.ctx.name || TranslationKeyMap._842_N_A;
        }
        return null;
    }

    onSortClick(column: Column): void {
        // TODO move
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        const ch = this._grid.grid
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            .filter((r) => r.def.ctx && r.def.ctx.children)
            .reduce((p, c) => p.concat(c.def.ctx.children), []);
        // eslint-disable-next-line @typescript-eslint/prefer-includes
        this._grid.grid.sort(
            this.dataSource.sortDefs(column),
            // eslint-disable-next-line @typescript-eslint/prefer-includes
            (row) => row.def.type !== RowType.HEADER && ch.indexOf(row) === -1,
        );
        const head = this._grid.grid.find((r) => r.def.type === RowType.HEADER);
        head.def.cells.forEach((cell) => {
            cell.sortClassList = '';
            if (
                !this.dataSource.lastSort ||
                this.dataSource.lastSort.columnId !== cell.columnId
            ) {
                // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                cell.sortClassList += '';
            } else {
                if (this.dataSource.lastSort.order === SortOrder.ASC) {
                    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                    cell.sortClassList += 'chevron-up';
                }
                if (this.dataSource.lastSort.order === SortOrder.DESC) {
                    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                    cell.sortClassList += 'chevron-down';
                }
            }
        });
        head.detectChanges();
    }

    onExpandClick($event, c: Column, r: RowNode, l: CellNode, ctx: any): void {
        $event.stopPropagation();

        if (r.def.type === RowType.ENTRY) {
            this._store.dispatch(
                formResetState(
                    this.mode === Mode.MEMBER ? 'MEMBER' : 'MANAGER',
                ),
            );
        }

        if (!r.def.ctx.expanded) {
            if (r.def.type === RowType.ENTRY) {
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                this._grid.grid
                    .filter(
                        (rr) =>
                            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                            rr.def.ctx &&
                            rr.def.ctx.expanded &&
                            rr.def.type === RowType.ENTRY,
                    )
                    .forEach((rr) => {
                        rr.removeChildren();
                        rr.def.ctx.expanded = false;
                        rr.detectChanges();
                    });
            }
            const rows = this.dataSource.makeRows(r.def);
            // if (this.mode === Mode.MANAGER && rows.length === 0) {
            //     rows.push(this.dataSource.makeNoDataRow(0));
            // }
            r.setChildren(rows);
            r.def.ctx.expanded = true;
            r.detectChanges();
        } else {
            r.removeChildren();
            r.def.ctx.expanded = false;
            r.detectChanges();
        }
    }

    onPayloadChange($event: PayloadExtended, row: RowNode): void {
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        const pushUp = (r, ctx) => {
            if (!(r instanceof RowNode)) return;
            if (r.parent instanceof RowNode) {
                pushUp(r.parent, ctx);
            }
            r.def.cells.forEach((c) => {
                c.ctx.lastModified = lastModifiedFor([
                    ctx.created as Date,
                    ctx.lastModified as Date,
                ]);
                c.ctx.status = statusFor(ctx.flowState, this.mode);

                if (r.def.type === RowType.ENTRY) {
                    // TODO revise merge
                    c.ctx.threadGuid = ctx.threadGuid;
                }

                if (r.def.type === RowType.USER) {
                    // TODO revise merge
                    if (c.ctx.payload) {
                        c.ctx.payload.threadGuid = ctx.threadGuid;
                    }
                }
            });
            r.detectChanges({ onlySelf: true });
        };

        if (
            isWithinCurrentDateRange(
                $event.created as Date,
                this.metadata.reviewDateRanges,
            )
        ) {
            pushUp(row.parent, $event);
        }
        row.detectChanges({ onlySelf: true });
    }
}
